import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  Label,
  MultiSelect,
  Text,
  Textarea,
  TextFormField,
} from "@/components/ui";
import { PhaseProjectBuilder } from "@/components/features/settings/defaults/matter-phase-projects/PhaseProjectBuilder";
import { PhaseProjectSummary } from "@/components/features/settings/defaults/matter-phase-projects/PhaseProjectSummary";
import { ReusablePhaseEditorModal } from "@/components/features/settings/defaults/matter-phase-projects/ReusablePhaseEditorModal";
import { ManualActionEditorModal } from "@/components/features/settings/defaults/matter-phase-projects/ManualActionEditorModal";
import { PhaseProjectLibraryView } from "@/components/features/settings/defaults/matter-phase-projects/PhaseProjectLibraryView";
import { PhaseActionTaskTemplatesModal } from "./PhaseActionTaskTemplatesModal";
import {
  useCreatePhaseProject,
  useRetroactivelyApplyPhaseProject,
  useUpdatePhaseProject,
} from "@/lib/data/react-query/matters/matter-phase";
import type {
  PhaseActionDefinition,
  PhaseProject,
  PhaseProjectInput,
  ReusableMatterPhase,
} from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Pencil } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  formSchema,
  projectToDraft,
  type FormValues,
  type ProjectDraft,
} from "./phase-project-editor-utils";

const PHASE_PROJECT_FORM_ID = "phase-project-editor-form";

export function PhaseProjectEditor({
  project,
  incidentTypes,
  claimTypes,
  reusablePhases,
  actionDefinitions,
  readOnly,
  canCreateReusablePhase,
  canCreateAction,
  startEditing,
}: {
  project?: PhaseProject;
  incidentTypes: { id: string; label: string }[];
  claimTypes: { id: string; label: string }[];
  reusablePhases: ReusableMatterPhase[];
  actionDefinitions: PhaseActionDefinition[];
  readOnly: boolean;
  canCreateReusablePhase: boolean;
  canCreateAction: boolean;
  startEditing?: boolean;
}) {
  const navigate = useNavigate({ from: "/matters/settings/phase-projects" });
  const [draft, setDraft] = useState<ProjectDraft>(() =>
    projectToDraft(project),
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | undefined>(
    () => draft.phases[0]?.localId,
  );
  const [isEditing, setIsEditing] = useState(
    !readOnly && (!project || Boolean(startEditing)),
  );
  const [baseline, setBaseline] = useState(() =>
    JSON.stringify(projectToDraft(project)),
  );
  const [exitRequested, setExitRequested] = useState(false);
  const [createPhaseOpen, setCreatePhaseOpen] = useState(false);
  const [createActionForPhaseId, setCreateActionForPhaseId] =
    useState<string>();
  const [editingActionId, setEditingActionId] = useState<string>();
  const [confirmCancelEdit, setConfirmCancelEdit] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showSampleData, setShowSampleData] = useState(true);
  const [showLibrary, setShowLibrary] = useState(false);
  const [inlineCreatedPhases, setInlineCreatedPhases] = useState<
    ReusableMatterPhase[]
  >([]);
  const [inlineCreatedActions, setInlineCreatedActions] = useState<
    PhaseActionDefinition[]
  >([]);
  const [retroactiveTarget, setRetroactiveTarget] = useState<{
    projectId: string;
    projectRevisionId: string;
  }>();
  const leavingAfterConfirmation = useRef(false);
  const createProject = useCreatePhaseProject();
  const updateProject = useUpdatePhaseProject();
  const retroactivelyApplyProject = useRetroactivelyApplyPhaseProject();
  const isPending = createProject.isPending || updateProject.isPending;
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: project?.title ?? "" },
    mode: "onSubmit",
  });
  useEffect(() => {
    if (exitRequested)
      void navigate({
        to: "/matters/settings/phase-projects",
        search: (previous) => ({
          ...previous,
          editor: undefined,
          edit: undefined,
        }),
        replace: true,
      });
  }, [exitRequested, navigate]);
  useEffect(() => {
    if (!project || !startEditing || isEditing) return;
    void navigate({
      to: "/matters/settings/phase-projects",
      search: (previous) => ({ ...previous, edit: undefined }),
      replace: true,
    });
  }, [isEditing, navigate, project, startEditing]);
  const availableReusablePhases = [
    ...reusablePhases,
    ...inlineCreatedPhases.filter(
      (created) => !reusablePhases.some((phase) => phase.id === created.id),
    ),
  ];
  const availableActionDefinitions = [
    ...actionDefinitions,
    ...inlineCreatedActions.filter(
      (created) =>
        !actionDefinitions.some((action) => action.id === created.id),
    ),
  ];
  const selectedPhaseIds = new Set(
    draft.phases.map((phase) => phase.reusablePhaseId),
  );
  const isDirty = JSON.stringify(draft) !== baseline;
  const shouldGuardExit =
    !readOnly && (Boolean(retroactiveTarget) || (isEditing && isDirty));
  const blocker = useBlocker({ condition: !exitRequested && shouldGuardExit });

  useEffect(() => {
    if (!shouldGuardExit || exitRequested) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [shouldGuardExit, exitRequested]);

  const submit = async ({ title }: FormValues) => {
    const shouldOfferRetroactiveApplication =
      project?.lifecycle === "Active" && isDirty;
    if (new Set(draft.incidentTypeIds).size !== draft.incidentTypeIds.length) {
      toast.error("Each incident type can only be selected once.");
      return;
    }
    if (selectedPhaseIds.size !== draft.phases.length) {
      toast.error("Each reusable phase can only be used once per project.");
      return;
    }
    const actionIds = draft.phases.flatMap(
      (phase) => phase.actionDefinitionIds,
    );
    if (
      actionIds.reduce(
        (count, id) => count + (draft.actionTaskTemplates?.[id]?.length ?? 0),
        0,
      ) > 100
    ) {
      toast.error("A project supports up to 100 automatic tasks.");
      return;
    }
    if (new Set(actionIds).size !== actionIds.length) {
      toast.error("Each action can only be used once per project.");
      return;
    }
    if (
      draft.phases.some(
        (phase) =>
          phase.claimTypeIds.length > 0 &&
          phase.actionDefinitionIds.some((actionId) => {
            const actionClaimTypeIds = phase.actionClaimTypeIds[actionId] ?? [];
            return (
              actionClaimTypeIds.length > 0 &&
              actionClaimTypeIds.some((id) => !phase.claimTypeIds.includes(id))
            );
          }),
      )
    ) {
      toast.error(
        "Each action's claim types must be within its phase's claim types.",
      );
      return;
    }
    if (
      draft.phases.some(
        (phase) =>
          phase.claimTypeIds.length > 0 &&
          phase.widgets.some(
            (widget) =>
              (widget.claimTypeIds?.length ?? 0) > 0 &&
              widget.claimTypeIds?.some(
                (id) => !phase.claimTypeIds.includes(id),
              ),
          ),
      )
    ) {
      toast.error(
        "Each widget's claim types must be within its phase's claim types.",
      );
      return;
    }
    if (project?.lifecycle === "Active") {
      if (draft.incidentTypeIds.length === 0) {
        toast.error("An active project requires at least one incident type.");
        return;
      }
      if (draft.phases.length === 0) {
        toast.error("An active project requires at least one phase.");
        return;
      }
      if (
        draft.phases.some(
          (phase) =>
            phase.actionDefinitionIds.length === 0 &&
            phase.widgets.length === 0,
        )
      ) {
        toast.error(
          "Every phase in an active project requires at least one action or widget.",
        );
        return;
      }
    }

    if (
      draft.phases.some((phase) =>
        phase.widgets.some(
          (widget) =>
            !widget.title.trim() ||
            (widget.type === "ActionGroup" &&
              widget.actionDefinitionIds.length === 0) ||
            (widget.type === "Countdown" &&
              (!Number.isInteger(widget.durationDays) ||
                widget.durationDays < 1 ||
                widget.durationDays > 3650)),
        ),
      )
    ) {
      toast.error(
        "Give each widget a title, add at least one action to each group, and set countdowns to 1–3650 whole days.",
      );
      return;
    }

    const payload: PhaseProjectInput = {
      actionTaskTemplates: Object.fromEntries(
        actionIds.map((id) => [id, draft.actionTaskTemplates?.[id] ?? []]),
      ),
      title,
      description: draft.description?.trim() || null,
      incidentTypeIds: draft.incidentTypeIds,
      phases: draft.phases.map((phase) => ({
        reusablePhaseId: phase.reusablePhaseId,
        teamId: phase.teamId ?? null,
        userId: phase.userId ?? null,
        roleId: phase.roleId ?? null,
        actionDefinitionIds: phase.actionDefinitionIds,
        claimTypeIds: phase.claimTypeIds,
        actionClaimTypeIds: Object.fromEntries(
          phase.actionDefinitionIds.map((actionId) => [
            actionId,
            phase.actionClaimTypeIds[actionId] ?? [],
          ]),
        ),
        widgets: phase.widgets.map((widget) => ({
          ...widget,
          title: widget.title.trim(),
        })),
      })),
    };
    const response = project
      ? await updateProject.mutateAsync({
          ...payload,
          projectId: project.id,
          dateModified: project.dateModified,
        })
      : await createProject.mutateAsync(payload);
    const selectedReusablePhaseId = draft.phases.find(
      (phase) => phase.localId === selectedPhaseId,
    )?.reusablePhaseId;
    const next = projectToDraft(response.data);
    setDraft(next);
    setSelectedPhaseId(
      next.phases.find(
        (phase) => phase.reusablePhaseId === selectedReusablePhaseId,
      )?.localId ?? next.phases[0]?.localId,
    );
    setBaseline(JSON.stringify(next));
    form.reset({ title: next.title });
    if (shouldOfferRetroactiveApplication) {
      setRetroactiveTarget({
        projectId: response.data.id,
        projectRevisionId: response.data.currentRevisionId,
      });
    } else if (project) {
      setIsEditing(false);
      setDetailsOpen(false);
    } else {
      setExitRequested(true);
    }
  };

  const backToProjects = () =>
    void navigate({
      to: "/matters/settings/phase-projects",
      search: (previous) => ({
        ...previous,
        editor: undefined,
        edit: undefined,
      }),
      replace: true,
    });

  const cancelEdit = () => {
    if (!project) {
      backToProjects();
      return;
    }
    const savedDraft = JSON.parse(baseline) as ProjectDraft;
    setDraft(savedDraft);
    setSelectedPhaseId((current) =>
      savedDraft.phases.some((phase) => phase.localId === current)
        ? current
        : savedDraft.phases[0]?.localId,
    );
    form.reset({ title: savedDraft.title });
    setIsEditing(false);
    setDetailsOpen(false);
  };
  const requestCancelEdit = () => {
    if (isDirty) {
      setConfirmCancelEdit(true);
    } else {
      cancelEdit();
    }
  };

  const projectFields = (
    <fieldset
      disabled={readOnly || isPending || Boolean(retroactiveTarget)}
      className={
        project
          ? "grid min-w-0 gap-4 md:grid-cols-2"
          : "bg-card grid min-w-0 gap-4 rounded-xl border p-5 sm:p-6 md:grid-cols-2"
      }
    >
      <Form {...form}>
        <form
          id={PHASE_PROJECT_FORM_ID}
          className="contents"
          onSubmit={form.handleSubmit(submit, () => {
            setDetailsOpen(true);
          })}
        >
          <div className="min-w-0">
            <TextFormField
              label="Project title*"
              maxLength={200}
              register={form.register("title")}
              handleChange={(title) => {
                setDraft((current) => ({
                  ...current,
                  title,
                }));
              }}
              placeholder="Personal injury workflow"
            />
          </div>
        </form>
      </Form>
      <div className="min-w-0 space-y-2">
        <Label>Incident types</Label>
        <MultiSelect
          className="min-w-0"
          modalPopover
          maxCount={2}
          showClearExtraOptions={false}
          options={incidentTypes.map((incidentType) => ({
            label: incidentType.label,
            value: incidentType.id,
          }))}
          selectedValues={draft.incidentTypeIds}
          setSelectedValues={(nextIncidentTypeIds) => {
            setDraft((current) => ({
              ...current,
              incidentTypeIds:
                typeof nextIncidentTypeIds === "function"
                  ? nextIncidentTypeIds(current.incidentTypeIds)
                  : nextIncidentTypeIds,
            }));
          }}
          onValueChange={(incidentTypeIds) => {
            setDraft((current) => ({ ...current, incidentTypeIds }));
          }}
          placeholder="Select incident types"
        />
      </div>
      <div className="md:col-span-2">
        <div className="space-y-2">
          <Label htmlFor="phase-project-description">Description</Label>
          <Textarea
            id="phase-project-description"
            value={draft.description ?? ""}
            maxLength={5000}
            rows={2}
            onChange={(event) => {
              setDraft((current) => ({
                ...current,
                description: event.target.value,
              }));
            }}
            placeholder="Add description"
          />
        </div>
      </div>
    </fieldset>
  );
  const renderPhaseBuilder = (
    builderDraft: ProjectDraft,
    editing: boolean,
    idPrefix: string,
  ) => (
    <PhaseProjectBuilder
      key={project?.id ?? "new"}
      selectedPhaseId={selectedPhaseId}
      onSelectedPhaseIdChange={setSelectedPhaseId}
      actionTaskTemplates={builderDraft.actionTaskTemplates}
      onEditActionTasks={(actionId) => {
        if (!isPending) setEditingActionId(actionId);
      }}
      phases={builderDraft.phases}
      setPhases={(phases) => {
        setDraft((current) => ({ ...current, phases }));
      }}
      reusablePhases={availableReusablePhases}
      actionDefinitions={availableActionDefinitions}
      claimTypes={claimTypes}
      readOnly={readOnly || isPending || !editing}
      canCreatePhase={canCreateReusablePhase}
      onCreatePhase={() => {
        setCreatePhaseOpen(true);
      }}
      canCreateAction={canCreateAction}
      onCreateAction={setCreateActionForPhaseId}
      stacked={Boolean(project && editing)}
      preview={Boolean(project && !editing)}
      embedded={Boolean(project)}
      idPrefix={idPrefix}
      showSampleData={showSampleData}
      onShowSampleDataChange={setShowSampleData}
    />
  );
  const savedDraft = project ? (JSON.parse(baseline) as ProjectDraft) : draft;

  if (showLibrary) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-6 pb-8">
        <PhaseProjectLibraryView
          onBack={() => {
            setShowLibrary(false);
          }}
          backLabel="Project editor"
        />
      </div>
    );
  }

  return (
    <>
      {project ? (
        <div className="mx-auto w-full max-w-7xl space-y-3 pb-8">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-2"
            disabled={isPending}
            onClick={backToProjects}
          >
            <ArrowLeft className="size-4" /> Phase projects
          </Button>
          <div className="bg-card @container min-w-0 rounded-xl border shadow-sm">
            <PhaseProjectSummary
              draft={savedDraft}
              incidentTypes={incidentTypes}
              action={
                !readOnly ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setIsEditing(true);
                    }}
                    aria-label="Edit project"
                    title="Edit project"
                  >
                    <Pencil className="size-4" aria-hidden="true" />
                  </Button>
                ) : null
              }
            />
            {renderPhaseBuilder(savedDraft, false, "preview-")}
          </div>
        </div>
      ) : (
        <div className="mx-auto w-full max-w-7xl space-y-6 pb-8">
          <div className="space-y-3">
            <Button
              type="button"
              variant="ghost"
              className="-ml-3 gap-2"
              disabled={isPending}
              onClick={backToProjects}
            >
              <ArrowLeft className="size-4" /> Phase projects
            </Button>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <Text variant="title">Create phase project</Text>
                <Text variant="subHeading">
                  Build an ordered set of phases and actions. Drafts may be
                  saved before they are ready to activate.
                </Text>
              </div>
              {!readOnly ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowLibrary(true);
                  }}
                >
                  Manage phases and actions
                </Button>
              ) : null}
            </div>
          </div>
          <div className="space-y-6">
            {projectFields}
            {renderPhaseBuilder(draft, true, "create-")}
          </div>
          <div className="bg-background/95 sticky bottom-0 z-10 flex justify-end gap-2 border-t py-4 backdrop-blur-sm">
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={cancelEdit}
            >
              {readOnly ? "Close" : "Cancel"}
            </Button>
            {!readOnly ? (
              <Button
                type="submit"
                form={PHASE_PROJECT_FORM_ID}
                disabled={isPending}
              >
                Create draft
              </Button>
            ) : null}
          </div>
        </div>
      )}
      {project ? (
        <AppModal
          open={isEditing}
          onOpenChange={(open) => {
            if (!open) requestCancelEdit();
          }}
          title="Edit phase project"
          description="Edit the project details, phases, actions, and widgets. Save the project when you are done."
          size="4xl"
          className="h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)]"
          bodyClassName="bg-muted/15"
          loading={isPending}
          footer={
            <div className="flex w-full justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={requestCancelEdit}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={isPending}
                onClick={() => {
                  void form.handleSubmit(submit, () => {
                    setDetailsOpen(true);
                  })();
                }}
              >
                {isPending ? "Saving..." : "Save project"}
              </Button>
            </div>
          }
        >
          <div className="@container mx-auto w-full min-w-0 space-y-4">
            <div className="rounded-xl border">
              <PhaseProjectSummary
                draft={draft}
                incidentTypes={incidentTypes}
              />
              <Accordion
                type="single"
                collapsible
                value={detailsOpen ? "details" : ""}
                onValueChange={(value) => {
                  setDetailsOpen(value === "details");
                }}
              >
                <AccordionItem
                  value="details"
                  className="border-t border-b-0 px-5 sm:px-6"
                >
                  <AccordionTrigger>Project details</AccordionTrigger>
                  <AccordionContent className="pt-2">
                    {projectFields}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            {renderPhaseBuilder(draft, true, "edit-")}
          </div>
        </AppModal>
      ) : null}
      {editingActionId ? (
        <PhaseActionTaskTemplatesModal
          actionTitle={
            availableActionDefinitions.find(
              (action) => action.id === editingActionId,
            )?.title ?? "Action"
          }
          templates={
            (isEditing ? draft : savedDraft).actionTaskTemplates?.[
              editingActionId
            ] ?? []
          }
          readOnly={readOnly || isPending || (Boolean(project) && !isEditing)}
          onClose={() => {
            setEditingActionId(undefined);
          }}
          onChange={(templates) => {
            setDraft((current) => ({
              ...current,
              actionTaskTemplates: {
                ...current.actionTaskTemplates,
                [editingActionId]: templates,
              },
            }));
          }}
        />
      ) : null}
      <ReusablePhaseEditorModal
        stacked={Boolean(project)}
        open={createPhaseOpen}
        onOpenChange={setCreatePhaseOpen}
        onCreated={(phase) => {
          setInlineCreatedPhases((current) => [...current, phase]);
          setDraft((current) => ({
            ...current,
            phases: [
              ...current.phases,
              {
                localId: crypto.randomUUID(),
                reusablePhaseId: phase.id,
                actionDefinitionIds: [],
                claimTypeIds: [],
                actionClaimTypeIds: {},
                widgets: [],
              },
            ],
          }));
        }}
      />
      <ManualActionEditorModal
        stacked={Boolean(project)}
        open={Boolean(createActionForPhaseId)}
        onOpenChange={(open) => {
          if (!open) setCreateActionForPhaseId(undefined);
        }}
        onCreated={(action) => {
          setInlineCreatedActions((current) => [...current, action]);
          setDraft((current) => ({
            ...current,
            phases: current.phases.map((phase) =>
              phase.localId === createActionForPhaseId
                ? {
                    ...phase,
                    actionDefinitionIds: [
                      ...phase.actionDefinitionIds,
                      action.id,
                    ],
                    actionClaimTypeIds: {
                      ...phase.actionClaimTypeIds,
                      [action.id]: [],
                    },
                  }
                : phase,
            ),
          }));
        }}
      />
      <ActionConfirmationModal
        open={confirmCancelEdit}
        setOpen={setConfirmCancelEdit}
        title="Discard unsaved changes?"
        description="Your changes to this phase project have not been saved."
        buttonText="Discard changes"
        actionVariant="destructive"
        afterActionFn={cancelEdit}
      />
      <ActionConfirmationModal
        open={Boolean(retroactiveTarget)}
        setOpen={(nextOpen) => {
          if (!nextOpen) {
            setRetroactiveTarget(undefined);
            setIsEditing(false);
          }
        }}
        title="Apply changes to existing matters?"
        description="This will queue the saved project revision for every existing matter with one of this project's incident types. Each matter's claim types determine which phases, actions, and widgets apply. Existing manual action progress, overrides, and phase assignments are preserved when their phase or action remains in the project."
        cancelText="No, future matters only"
        buttonText="Yes, apply changes"
        actionVariant="default"
        isActionPending={retroactivelyApplyProject.isPending}
        onCancel={() => {
          setRetroactiveTarget(undefined);
          setIsEditing(false);
        }}
        afterActionFn={async () => {
          if (!retroactiveTarget) return;
          await retroactivelyApplyProject.mutateAsync({
            projectId: retroactiveTarget.projectId,
            expectedPhaseProjectRevisionId: retroactiveTarget.projectRevisionId,
          });
          setRetroactiveTarget(undefined);
          setIsEditing(false);
        }}
      />
      <ActionConfirmationModal
        open={blocker.status === "blocked"}
        setOpen={(open) => {
          if (
            !open &&
            blocker.status === "blocked" &&
            !leavingAfterConfirmation.current
          )
            blocker.reset();
        }}
        title={
          retroactiveTarget
            ? "Leave without applying changes?"
            : "Discard unsaved changes?"
        }
        description={
          retroactiveTarget
            ? "The project was saved, but you have not chosen whether to apply it to existing matters."
            : "Your changes to this phase project have not been saved."
        }
        buttonText="Leave page"
        actionVariant="destructive"
        afterActionFn={() => {
          if (blocker.status === "blocked") {
            leavingAfterConfirmation.current = true;
            blocker.proceed();
          }
        }}
      />
    </>
  );
}
